import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, getAccessToken } from '@theraply/api-client';
import { Spinner } from '@theraply/ui';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'therapist' | 'admin';
}

export default function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const hasToken = !!getAccessToken();

  // Show loading spinner while checking authentication
  if (isLoading || (hasToken && !user && !isAuthenticated)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirement
  if (requiredRole && user.role !== requiredRole) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Access Denied</h1>
          <p className="mt-2 text-gray-600">
            You do not have permission to access this page.
          </p>
          <a
            href="/"
            className="mt-4 inline-block text-primary-600 hover:text-primary-500"
          >
            Return to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Higher-order component to require authentication
 */
export function withAuth<P extends object>(
  Component: React.ComponentType<P>,
  requiredRole?: 'therapist' | 'admin'
) {
  return function ProtectedComponent(props: P) {
    return (
      <ProtectedRoute requiredRole={requiredRole}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
}
